import React, { useState, memo } from 'react'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import SearchIcon from '@mui/icons-material/Search'
import Typography from '@mui/material/Typography'
import AddCompanyDialog from 'proposals/components/AddCompanyDialog'
import { useTranslation } from 'react-i18next'
import { useSearchCompanies } from 'vendors/vendorWizard/selectCompanyStep/queries/useCompaniesQuery'
import { useSnackbar } from 'snackbar/SnackbarContext'

const PREFIX = 'SelectCompany'

const classes = {
  popupIndicator: `${PREFIX}-popupIndicator`,
  autocomplete: `${PREFIX}-autocomplete`,
  helperRow: `${PREFIX}-helperRow`,
  addCompanyLink: `${PREFIX}-addCompanyLink`
}

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ }) => ({
  [`& .${classes.popupIndicator}`]: {
    transform: 'rotate(0deg)'
  },

  [`& .${classes.autocomplete}`]: {
    flex: 1
  },

  [`& .${classes.helperRow}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
    paddingLeft: 12,
    paddingRight: 12
  },

  [`& .${classes.addCompanyLink}`]: {
    cursor: 'pointer'
  }
}))

export type CompanyType = {
  name: string
  businessId: string
}

type SelectTargetPropTypes = {
  selectCompany: (value: CompanyType | null) => void
  selectedValue: CompanyType | null
  region: string
  label?: string
  disabled?: boolean
}

const SelectCompany: React.FC<SelectTargetPropTypes> = ({ selectCompany, selectedValue, region, label, disabled }) => {
  const [searchWord, setSearchWord] = useState<string>('')
  const [isAddCompanyOpen, setIsAddCompanyOpen] = useState<boolean>(false)
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const searchQueryInfo = useSearchCompanies(searchWord, region)

  const onInputChange = (_event: unknown, value: string): void => {
    setSearchWord(value)
  }

  const onCompanyChange = (_event: React.ChangeEvent<unknown>, value: CompanyType | null): void => {
    selectCompany(value)
  }

  const toggleAddCompanyDialog = React.useCallback(() => {
    setIsAddCompanyOpen(!isAddCompanyOpen)
  }, [isAddCompanyOpen])

  const options = searchQueryInfo.data ? (searchQueryInfo.data as CompanyType[]) : []

  React.useEffect(() => {
    if (selectedValue && !selectedValue.name && !isAddCompanyOpen) {
      toggleAddCompanyDialog()
    }
  }, [isAddCompanyOpen, selectedValue, toggleAddCompanyDialog])

  React.useEffect(() => {
    if (searchQueryInfo.isError) {
      showSnackbar(t('company-search-error-text'), 'error', t('company-search-error-title'))
    }
  }, [searchQueryInfo.isError, showSnackbar, t])


  return (
    <Root>
      <Autocomplete
        classes={{
          popupIndicatorOpen: classes.popupIndicator
        }}
        filterOptions={(x) => x}
        value={selectedValue}
        loading={searchQueryInfo.isLoading}
        className={classes.autocomplete}
        onChange={disabled ? undefined : onCompanyChange}
        onInputChange={disabled ? undefined : onInputChange}
        options={disabled ? [] : options}
        disablePortal={disabled}
        readOnly={disabled}
        isOptionEqualToValue={(option, value): boolean => option.businessId === value.businessId}
        getOptionLabel={(option): string => option.name ?? option.businessId}
        renderInput={(params): React.ReactNode => (
          <TextField
            {...params}
            variant="filled"
            label={label ?? t('vendor-company')}
            required
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              sx: {
                pt: '25px !important'
              }
            }}
            InputLabelProps={{
              sx: {
                transform: 'translate(12px, 20px) scale(1)',

                '&.MuiInputLabel-shrink': {
                  maxWidth: 'calc(133% - 55px)',
                  transform: 'translate(12px, 7px) scale(0.9)'
                }
              }
            }}
          />
        )}
        loadingText={t('searching')}
        noOptionsText={t('no-results')}
        popupIcon={
          <SearchIcon 
            sx={{ 
              opacity: disabled ? 0.38 : 1,
              cursor: disabled ? 'default' : 'pointer'
            }} 
          />
        }
      />
      {!disabled && (
        <div className={classes.helperRow}>
          <div className={classes.addCompanyLink} onClick={toggleAddCompanyDialog}>
            <Typography variant="body2" color={'primary'}>
              {t('cant-find')}
            </Typography>
          </div>

          {selectedValue && (
            <Typography variant="body2" color="textSecondary">
              {`${t('business-id')}: ${selectedValue.businessId}`}
            </Typography>
          )}
        </div>)}
      <AddCompanyDialog
        open={isAddCompanyOpen}
        onClose={toggleAddCompanyDialog}
        addCompany={selectCompany}
        initialBusinessId={!selectedValue?.name ? selectedValue?.businessId : ''}
      />
    </Root>
  )
}

export default memo(SelectCompany)
